import { delay } from 'redux-saga'
import { takeLatest, call, put, all, select } from 'redux-saga/effects'
import { normalize } from 'normalizr'
import { push as pushHistory } from 'react-router-redux'
import { setActiveLanguage } from 'react-localize-redux'

import actions, { constants } from '../actions/auth'
import systemsActions from '../actions/systems'
import snackbarActions from 'common/actions/snackbar'
import * as schemas from 'schemas'
import * as api from 'api/auth'
import * as userApi from 'api/user'
import * as Routes from 'constants/Routes'
import * as LocalizeStore from 'utils/localizeStore'
import { setUserMeasurementSystem } from 'utils/auxStore'
import {
  isSessionOverLimit,
  reportSessionLimitReached,
  reportSessionRejected
} from 'utils/sessionLimit'
import { isSessionEndedError } from 'utils/session'
import { isPublicPath } from 'utils/publicPages'

function* postAuth(routing) {
  if (routing.locationBeforeTransitions.pathname === Routes.MAIN) {
    return yield put(pushHistory(Routes.PROJECTS))
  }
  yield put(pushHistory(routing.locationBeforeTransitions))
}

/**
 * Reads the username claim out of the session tokens.
 *
 * @param {Object} tokens - Tokens from getCognitoSession.
 * @returns {string|undefined} The username, or undefined when there is none.
 */
const getUsernameFromTokens = tokens => tokens?.accessToken?.payload?.username

// How often to read the session on page load before giving up, and how long
// to wait between tries. A try only fails like this when the renewal hits a
// passing error, such as a network error or a Cognito service error.
const SESSION_ATTEMPTS = 3
const SESSION_RETRY_DELAY = 2000

/**
 * Reads the session on page load, and tries again after a passing error.
 *
 * Amplify keeps the session when a renewal fails for a passing reason, such as
 * the network, so giving up at once would send a signed-in user to the login
 * page. "Nobody is signed in" and errors that end the session are final, so
 * they are not tried again.
 *
 * @returns {Object} The session tokens.
 * @throws The last error when every try fails, or the final error at once.
 */
export function* getSessionOnLoad() {
  for (let attempt = 1; ; attempt++) {
    try {
      return yield call(api.getCognitoSession)
    } catch (err) {
      const isFinal = err?.name === api.NO_SESSION_ERROR || isSessionEndedError(err)
      if (isFinal || attempt >= SESSION_ATTEMPTS) throw err
      yield call(delay, SESSION_RETRY_DELAY)
    }
  }
}

export function* onAuthUser() {
  yield put(actions.authUserRequest.start())
  const routing = yield select(state => state.routing)
  if (isPublicPath(routing.locationBeforeTransitions.pathname)) {
    return yield put(actions.authUserRequest.failure(null))
  }

  // Amplify keeps the session and renews its token when needed. No session
  // means nobody is signed in. A passing error is tried again first, and if it
  // still fails the user signs in again. When a session could not be renewed,
  // the tokenRefresh_failure listener has already sent the user to the login
  // page.
  let tokens
  try {
    tokens = yield call(getSessionOnLoad)
  } catch (err) {
    yield put(actions.authUserRequest.failure())

    return yield call(postAuth, routing)
  }

  // A session past its deadline ends here instead of loading the user. The
  // listener signs the user out and shows the message.
  if (yield call(isSessionOverLimit, tokens)) {
    yield put(actions.authUserRequest.failure())

    return yield call(reportSessionLimitReached)
  }

  try {
    const { user } = yield call(userApi.getCognitoUser, {
      username: getUsernameFromTokens(tokens)
    }) // Pedimos el usuario de la Dynamo
    setUserMeasurementSystem(user.measurementSystem || 'mS')
    yield put(setActiveLanguage(user.language))
    yield call(LocalizeStore.setAuthStoreData, user.language)
    const norm = yield call(normalize, user, schemas.user)
    yield put(actions.authUserRequest.success(norm))
    yield call(postAuth, routing)
  } catch (err) {
    yield put(actions.authUserRequest.failure(err))
    // Without the user the app cannot open, so the session ends here. The
    // listener signs the user out of Cognito and shows the message that waits
    // for the user. Opening the login page here would keep the tokens, and the
    // next page load would fail the same way.
    yield call(reportSessionRejected)
  }
}

export function* onLoginCognito({ payload: data }) {
  // Clears the message that sent the user here, such as the end of the last
  // session: they are answering it, so it has done its job.
  yield put(snackbarActions.hideSnackbar())
  yield put(actions.loginRequest.start())

  try {
    const { isSignedIn, nextStep } = yield call(api.loginCognito, data)

    // v4 threw UserNotConfirmedException for an unconfirmed account. v6 does not
    // throw: it reports CONFIRM_SIGN_UP as the next step. We pass that on as a
    // needsConfirmation flag so LoginForm can offer to resend the code. Any
    // other unfinished step is one we do not support, so it stays an error.
    if (!isSignedIn) {
      yield put(
        actions.loginRequest.failure(
          nextStep?.signInStep === 'CONFIRM_SIGN_UP' ? { needsConfirmation: true } : { nextStep }
        )
      )
    } else {
      const tokens = yield call(api.getCognitoSession)

      const { user } = yield call(userApi.getCognitoUser, {
        username: getUsernameFromTokens(tokens)
      }) // Pedimos el usuario de la Dynamo
      setUserMeasurementSystem(user.measurementSystem || 'mS')
      yield put(setActiveLanguage(user.language))
      yield call(LocalizeStore.setAuthStoreData, user.language)
      const norm = yield call(normalize, user, schemas.user)

      yield put(actions.loginRequest.success(norm))
      yield put(pushHistory(Routes.PROJECTS))
    }
  } catch (err) {
    yield put(actions.loginRequest.failure(err))
  }
}

export function* onSignUpTrustedUserCognito({ payload: data }) {
  yield put(actions.signUpTrustedUserRequest.start())

  try {
    // v6 signUpCognito throws when the account cannot be created, so reaching
    // the next line means Cognito accepted it.
    yield call(api.signUpCognito, data)
    yield call(api.signUpTrustedUser, data) // Si el registo en cognito ha sido correcto, lo metemos también en Dynamo DB
    yield put(actions.signUpTrustedUserRequest.success())

    yield put(snackbarActions.showSnackbar('snackbar.confirmSignUpTrustedUserSuccess'))

    yield put(
      pushHistory({
        pathname:
          Routes.CONFIRM_TRUSTED_USER +
          '/' +
          data.email +
          '/' +
          data.mainEmail +
          '/' +
          data.projectId
      })
    )
  } catch (err) {
    yield put(actions.signUpTrustedUserRequest.failure(err))
  }
}

export function* onSignUpCognito({ payload: data }) {
  yield put(actions.signUpRequest.start())

  try {
    // v6 signUpCognito throws when the account cannot be created, so reaching
    // the next line means Cognito accepted it. It no longer returns the user,
    // and in this pool the username is the email, so we pass on what was typed.
    yield call(api.signUpCognito, data)
    yield call(api.signUp, data) // Si el registo en cognito ha sido correcto, lo metemos también en Dynamo DB
    yield put(actions.signUpRequest.success())
    yield put(snackbarActions.showSnackbar('snackbar.confirmationCodeSent'))
    yield put(
      pushHistory({
        pathname: Routes.CONFIRM_USER_INFO,
        state: { username: data.email }
      })
    )
  } catch (err) {
    yield put(actions.signUpRequest.failure(err))
  }
}

export function* onConfirmUser({ payload: data }) {
  yield put(actions.confirmUserRequest.start())
  try {
    // v6 confirmSignUp throws on a bad or expired code, so reaching the next
    // line means the account is confirmed.
    yield call(api.confirmSignUpCognito, data)
    yield put(actions.confirmUserRequest.success())
    yield put(snackbarActions.showSnackbar('snackbar.confirmSignUpSuccess'))
  } catch (err) {
    yield put(actions.confirmUserRequest.failure(err))
  }
}

export function* onResendConfirmationMail({ payload: data }) {
  yield put(actions.resendConfirmationMailRequest.start())
  try {
    // v6 resendSignUpCode throws when the code cannot be sent, so reaching the
    // next line means it went out.
    yield call(api.resendConfirmationMail, data.username)
    yield put(actions.resendConfirmationMailRequest.success())
    yield put(snackbarActions.showSnackbar('snackbar.resendConfirmationMailSuccess'))
  } catch (err) {
    yield put(actions.resendConfirmationMailRequest.failure(err))
    yield put(snackbarActions.showSnackbar('snackbar.resendConfirmationMailFailure'))
  }
}

export function* onLogoutCognito() {
  yield put(systemsActions.pollSystemsStop())
  try {
    yield call(api.logoutCognito)
  } catch (err) {
    // Signing out of Cognito can fail, for example on a bad Cognito config. We
    // swallow it on purpose: an error escaping here would take down every saga
    // watcher with it, so the login page would stop responding until the user
    // reloads. When only revoking the token fails, Amplify has still cleared
    // its local tokens. On a config error it may not have, but then Amplify
    // cannot read a session either.
  } finally {
    yield put(pushHistory(Routes.LOGIN))
  }
}

/**
 * Signs the user out when the session ends.
 *
 * The listener in index.js dispatches sessionExpired when Amplify fails to
 * renew the token with the refresh token, for example after the refresh token
 * expired or was revoked, and when the session reaches SESSION_MAX_AGE after
 * the sign in. The user goes to the login page with a message.
 *
 * On a public page, such as a shared channel, the user stays where they are,
 * because the page works without a session. We still sign out of Cognito: when
 * the session reached its time limit, Amplify still has the tokens, and a later
 * page would keep using them.
 */
export function* onSessionExpired() {
  const routing = yield select(state => state.routing)
  if (isPublicPath(routing.locationBeforeTransitions.pathname)) {
    try {
      yield call(api.logoutCognito)
    } catch (err) {
      // Same as onLogoutCognito: an error escaping here would stop every saga
      // watcher, and the public page does not need the session anyway.
    }
    return
  }

  yield put(actions.logout())
  // Sticky on purpose: the user did not ask for this message and is probably
  // looking somewhere else when it arrives, so it waits for them.
  yield put(snackbarActions.showStickySnackbar('snackbar.expiredToken'))
}

export default function* watchAuth() {
  yield all([
    takeLatest(constants.AUTH_USER, onAuthUser),
    takeLatest(constants.LOGIN, onLoginCognito),
    takeLatest(constants.SIGN_UP, onSignUpCognito),
    takeLatest(constants.SIGN_UP_TRUSTED_USER, onSignUpTrustedUserCognito),
    takeLatest(constants.CONFIRM_USER, onConfirmUser),
    takeLatest(constants.RESEND_CONFIRMATION_MAIL, onResendConfirmationMail),
    takeLatest(constants.LOGOUT, onLogoutCognito),
    takeLatest(constants.SESSION_EXPIRED, onSessionExpired)
  ])
}
